/**
 * Created by Yoyo Fang on 06/06/2020.
 * Description:
 *
 * ------ maintenance history ------
 */
import { Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of as observableOf, Observable } from 'rxjs';
import { catchError, switchMap, map, take } from 'rxjs/operators';

import {
    INTRALINKS_ACTIONS, IntralinksCreateAction, IntralinksUpdateScreenAction,
    IntralinksUpdateAction, IntralinksLoadWorkspacesFolders
} from './intralinks.actions';
import { ScreenService } from '../services/screen.service';
import { IntralinksService } from '../services/intralinks.service';
import { SavedScreen } from '../models/saved-screen.model';
import { SystemActionTypes } from '../redux/actions/system.actions';
import { IntralinksAdminVisibilityChange } from './intralinks.actions';
import { businessConstants } from '../constants/business.constants';

@Injectable()
export class IntralinksEffects {
    constructor(
        private _actions$: Actions,
        private _intralinkService: IntralinksService,
        private _screenService: ScreenService,
        private _store: Store<any>,
    ) { }

    addScreenEffect$: Observable<any> = createEffect(() => this._actions$.pipe(
        ofType(INTRALINKS_ACTIONS.CREATE_ACCOUNT),
        map(action => action as IntralinksCreateAction),
        map((action: IntralinksCreateAction) => {
            const payload = action.payload;
            const screen = new SavedScreen();
            screen.type = 'intralinks'; // businessConstants.screenType.intralink;
            screen.accessList = payload.recipients;
            screen.name = payload.alias || payload.username;
            screen.description = '';
            screen.isPublic = false;
            screen.visible = true;
            screen.isLandingPage = false;
            screen.configuration = action.payload.map2ServerModel();
            return screen;
        }),
        switchMap((screen: SavedScreen) => this._screenService.create(screen).pipe(
            map(response => {
                this._store.dispatch({
                    type: INTRALINKS_ACTIONS.ADD_SCREEN,
                    payload: response
                });
                return {
                    type: SystemActionTypes.ADD_SCREEN,
                    payload: response
                } as Action;
            }),
            catchError(err => {
                return this._detectSessionLimitError(err, observableOf({
                    type: INTRALINKS_ACTIONS.CREATE_ACCOUNT_FAIL,
                    payload: err
                }));
            }))
        )));

    updateScreenEffect$: Observable<any> = createEffect(() => this._actions$.pipe(
        ofType(INTRALINKS_ACTIONS.UPDATE_ACCOUNT),
        map(action => action as IntralinksUpdateAction),
        map(action => {
            const screen = new SavedScreen();
            screen.id = action.screenId;
            screen.type = 'intralinks';
            screen.accessList = action.payload.recipients;
            screen.name = action.payload.alias || action.payload.username;
            screen.description = '';
            screen.isPublic = false;
            screen.visible = true;
            screen.isLandingPage = false;
            screen.configuration = action.payload.map2ServerModel();
            return screen;
        }),
        switchMap((screen: SavedScreen) => this._screenService.update(screen).pipe(
            map(response => {
                const action = new IntralinksUpdateScreenAction();
                action.payload = screen;
                this._store.dispatch(action);
                return {
                    type: SystemActionTypes.EDIT_SCREEN,
                    payload: response
                };
            }),
            catchError(err => {
                return this._detectSessionLimitError(err, observableOf({
                    type: INTRALINKS_ACTIONS.UPDATE_ACCOUNT_FAIL,
                    payload: err
                }));
            }))
        )));

    loadWorkspaceFolderEffect$: Observable<any> = createEffect(() => this._actions$.pipe(
        ofType(INTRALINKS_ACTIONS.LOAD_WORKSPACE_FOLDER),
        map(action => action as IntralinksLoadWorkspacesFolders),
        map(action => action.payload),
        switchMap((payload: any) => {
            return this._intralinkService.getWorkspaces(payload.screenId).pipe(
                map((response) => {
                    const items = [];
                    if (response['workspace'] && response['workspace'].length > 0) {
                        response['workspace'].forEach(element => {
                            items.push({
                                name: element['workspace-name'],
                                id: element['workspace-id'],
                                workspaceId: element['workspace-id'],
                                type: 'workspace',
                                folders: []
                            });
                        });
                    }
                    return {
                        type: INTRALINKS_ACTIONS.WORKSPACE_LOADED,
                        payload: {
                            screenId: payload.screenId,
                            items: items
                        }
                    };
                }),
                catchError(err => {
                    return this._detectSessionLimitError(err, observableOf({
                        type: INTRALINKS_ACTIONS.WORKSPACE_LOAD_FAILED,
                        payload: err
                    }));
                })
            );
        })));

    workspaceLoadedEffect$: Observable<any> = createEffect(() => this._actions$.pipe(
        ofType(INTRALINKS_ACTIONS.WORKSPACE_LOADED),
        map((action: any) => action.payload),
        map((payload: any) => {
            payload.items.forEach(item => {
                this._intralinkService.getFolderOfWorkspace(payload.screenId, item.id).pipe(
                    take(1),
                    catchError(err => {
                        return this._detectSessionLimitError(err, observableOf(null));
                    })
                ).subscribe((response) => {
                    item = this._intralinkService.treeFolderMapper(item, item.id, response['folder']);
                    this._store.dispatch({
                        type: INTRALINKS_ACTIONS.FOLDER_LOADED,
                        payload: item
                    });
                });
            });
            return { type: INTRALINKS_ACTIONS.WORKSPACE_LOADED_FINISHED, payload: null };
        })));

    loadFolderContentEffect$: Observable<any> = createEffect(() => this._actions$.pipe(
        ofType(INTRALINKS_ACTIONS.LOAD_FOLDER_CONTENT),
        map((action: any) => action.payload),
        switchMap((payload: any) => {
            return this._intralinkService.getContentOfFolder(payload.screenId, payload.workspaceId, payload.folderId).pipe(
                map(response => {
                    return {
                        type: INTRALINKS_ACTIONS.FOLDER_CONTENT_LOADED,
                        payload: this._intralinkService.treeFileMapper(response, payload.folderId, payload.mapping)
                    };
                }),
                catchError(err => {
                    return this._detectSessionLimitError(err, observableOf({
                        type: INTRALINKS_ACTIONS.WORKSPACE_LOAD_FAILED,
                        payload: err
                    }));
                })
            );
        })));

    deleteScreenEffect$: Observable<any> = createEffect(() => this._actions$.pipe(
        ofType(INTRALINKS_ACTIONS.DELETE_SCREEN),
        map((action: any) => action.payload),
        switchMap((payload: any) => {
            return this._screenService.deleteById(payload.screenId).pipe(
                map(response => {
                    this._store.dispatch({
                        type: SystemActionTypes.DELETE_SCREEN,
                        payload: { id: payload.screenId }
                    });
                    return {
                        type: INTRALINKS_ACTIONS.DELETE_ACCOUNT_SUCCESS,
                        payload: payload
                    };
                }),
                catchError(err => {
                    return this._detectSessionLimitError(err, observableOf({
                        type: INTRALINKS_ACTIONS.DELETE_ACCOUNT_FAILED,
                        payload: err
                    }));
                })
            );
        })));

    adminIntralinksVisibilityEffect$: Observable<any> = createEffect(() => this._actions$.pipe(
        ofType(INTRALINKS_ACTIONS.SET_ADMIN_VISIBILITY),
        map((action: any) => action.payload),
        switchMap((payload: any) => {
            return this._intralinkService.setAdminVisibility(payload.intralinks).pipe(
                map(response => {
                    return new IntralinksAdminVisibilityChange(['intralinks', 'enable'], response, true, payload.isOnlyEnable);
                })
            );
        })));

    private _detectSessionLimitError(err, other: Observable<any>) {
        if (err.message && err.message.indexOf(businessConstants.intralinks.onSessionWarning.original) > -1) {
            return observableOf({
                type: INTRALINKS_ACTIONS.REACH_SESSION_LIMIT,
                payload: { message: businessConstants.intralinks.onSessionWarning.userReadable }
            });
        } else {
            return other;
        }
    }
}
